import { useId } from 'react'

export type StatusTone = 'primary' | 'destructive' | 'accent'

const TONE_VAR: Record<StatusTone, string> = {
  primary: 'var(--primary)',
  destructive: 'var(--destructive)',
  accent: 'var(--accent)',
}

const hsl = (token: string, alpha?: number) =>
  alpha === undefined ? `hsl(${token})` : `hsl(${token} / ${alpha})`

// Tema belirteçlerinden okunur: koyu ve açık temada ayrı renk yazmak gerekmez.
const C = {
  border: 'var(--border)',
  card: 'var(--card)',
  fg: 'var(--foreground)',
  muted: 'var(--muted-foreground)',
  primary: 'var(--primary)',
  destructive: 'var(--destructive)',
  accent: 'var(--accent)',
}

function Stage({ tone, uid, children }: { tone: StatusTone; uid: string; children: React.ReactNode }) {
  const t = TONE_VAR[tone]
  return (
    <svg
      viewBox="0 0 320 220"
      role="img"
      className="mx-auto block h-auto w-full max-w-[360px] overflow-visible"
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <radialGradient id={`${uid}-glow`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={hsl(t, 0.34)} />
          <stop offset="55%" stopColor={hsl(t, 0.1)} />
          <stop offset="100%" stopColor={hsl(t, 0)} />
        </radialGradient>
        <pattern id={`${uid}-dots`} width="14" height="14" patternUnits="userSpaceOnUse">
          <circle cx="1.5" cy="1.5" r="1.1" fill={hsl(C.muted, 0.5)} />
        </pattern>
        <radialGradient id={`${uid}-fade`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#fff" stopOpacity="1" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
        <mask id={`${uid}-mask`}>
          <rect width="320" height="220" fill={`url(#${uid}-fade)`} />
        </mask>
      </defs>
      <ellipse cx="160" cy="110" rx="170" ry="120" fill={`url(#${uid}-glow)`} />
      <rect width="320" height="220" fill={`url(#${uid}-dots)`} mask={`url(#${uid}-mask)`} />
      {children}
    </svg>
  )
}

function Rings({ r = [44, 72, 100], opacity = 0.7 }: { r?: number[]; opacity?: number }) {
  return (
    <g fill="none" stroke={hsl(C.border)} strokeOpacity={opacity}>
      {r.map((v, i) => (
        <circle key={v} cx="160" cy="110" r={v} strokeWidth="1.2" strokeDasharray={i === 1 ? '2 6' : undefined} />
      ))}
    </g>
  )
}

/** Sensei halkası: logodaki gibi açık uçlu halka. */
function Halka({ cx, cy, r, w, color, gap = 0.2, rotate = -40 }: {
  cx: number; cy: number; r: number; w: number; color: string; gap?: number; rotate?: number
}) {
  const c = 2 * Math.PI * r
  return (
    <g transform={`rotate(${rotate} ${cx} ${cy})`}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={hsl(color)} strokeWidth={w} strokeLinecap="round"
        strokeDasharray={`${c * (1 - gap)} ${c * gap}`} />
    </g>
  )
}

function SkeletonLines({ x, y, w }: { x: number; y: number; w: number }) {
  return (
    <g fill={hsl(C.muted, 0.45)}>
      <rect x={x} y={y} width={w * 0.8} height="5" rx="2.5" />
      <rect x={x} y={y + 11} width={w} height="5" rx="2.5" opacity="0.7" />
      <rect x={x} y={y + 22} width={w * 0.55} height="5" rx="2.5" opacity="0.5" />
    </g>
  )
}

export function NotFoundArt() {
  const uid = useId().replace(/:/g, '')
  return (
    <Stage tone="primary" uid={uid}>
      <Rings />
      <defs>
        <linearGradient id={`${uid}-sweep`} x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor={hsl(C.primary, 0)} />
          <stop offset="100%" stopColor={hsl(C.primary, 0.38)} />
        </linearGradient>
        <linearGradient id={`${uid}-num`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={hsl(C.fg)} />
          <stop offset="100%" stopColor={hsl(C.fg, 0.55)} />
        </linearGradient>
      </defs>
      <g className="ss-sweep">
        <path d="M160 110 L160 10 A100 100 0 0 1 231 39 Z" fill={`url(#${uid}-sweep)`} />
      </g>
      {/* iz: taşınan sayfaya giden noktalı yol */}
      <path d="M205 96 C 232 92, 246 76, 252 60" fill="none" stroke={hsl(C.primary, 0.8)} strokeWidth="1.6"
        strokeDasharray="1 5" strokeLinecap="round" />
      <g className="ss-float">
        <g transform="translate(236 18) rotate(10)">
          <rect width="56" height="68" rx="9" fill={hsl(C.card)} stroke={hsl(C.border)} strokeWidth="1.4" />
          <rect x="8" y="9" width="22" height="22" rx="6" fill={hsl(C.primary, 0.18)} />
          <path d="M14 20 l5 5 l8 -10" fill="none" stroke={hsl(C.primary)} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" opacity="0" />
          <SkeletonLines x={8} y={40} w={40} />
          <circle cx="50" cy="8" r="10" fill={hsl(C.primary)} />
          <text x="50" y="12.5" textAnchor="middle" fontSize="13" fontWeight="700" fill={hsl('200 95% 14%')}>?</text>
        </g>
      </g>
      <g fontFamily="'Geist Sans Variable', var(--font-sans), system-ui, sans-serif" fontWeight="700" fontSize="92"
        textAnchor="middle" fill={`url(#${uid}-num)`}>
        <text x="82" y="140">4</text>
        <text x="238" y="140">4</text>
      </g>
      <Halka cx={160} cy={110} r={33} w={11} color={C.primary} />
      <circle cx="160" cy="110" r="5" fill={hsl(C.primary)} />
    </Stage>
  )
}

export function ErrorArt() {
  const uid = useId().replace(/:/g, '')
  return (
    <Stage tone="destructive" uid={uid}>
      <Rings r={[60, 92]} opacity={0.5} />
      <defs>
        <linearGradient id={`${uid}-bolt`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={hsl('43 100% 62%')} />
          <stop offset="100%" stopColor={hsl(C.primary)} />
        </linearGradient>
      </defs>
      {/* kırık halka: iki yarı birbirinden ayrılmış */}
      <g className="ss-shake-a">
        <path d="M118 150 A52 52 0 0 1 150 60" fill="none" stroke={hsl(C.destructive)} strokeWidth="12" strokeLinecap="round" />
      </g>
      <g className="ss-shake-b">
        <path d="M178 62 A52 52 0 0 1 202 148" fill="none" stroke={hsl(C.destructive, 0.55)} strokeWidth="12" strokeLinecap="round" />
      </g>
      {/* şimşek */}
      <g className="ss-pulse">
        <path d="M168 72 L146 112 H164 L152 148 L184 100 H165 Z" fill={`url(#${uid}-bolt)`} stroke={hsl(C.card)} strokeWidth="2" strokeLinejoin="round" />
      </g>
      {/* kopan parçalar */}
      <g fill={hsl(C.destructive, 0.7)}>
        <rect x="96" y="82" width="9" height="9" rx="2.5" transform="rotate(18 100 86)" className="ss-float" />
        <rect x="226" y="124" width="7" height="7" rx="2" transform="rotate(-14 229 127)" className="ss-float-2" />
        <circle cx="214" cy="70" r="3.5" opacity="0.8" />
        <circle cx="108" cy="150" r="2.5" opacity="0.7" />
      </g>
      {/* durağan nabız çizgisi */}
      <path d="M30 186 H118 L130 170 L142 200 L152 186 H290" fill="none" stroke={hsl(C.muted, 0.55)} strokeWidth="1.8"
        strokeLinecap="round" strokeLinejoin="round" strokeDasharray="1 0" />
    </Stage>
  )
}

export function ForbiddenArt() {
  const uid = useId().replace(/:/g, '')
  return (
    <Stage tone="accent" uid={uid}>
      <Rings />
      {/* arkadaki kilitli kartlar */}
      <g transform="translate(22 52) rotate(-9)">
        <rect width="70" height="86" rx="10" fill={hsl(C.card)} stroke={hsl(C.border)} strokeWidth="1.4" />
        <rect x="10" y="12" width="26" height="26" rx="7" fill={hsl(C.accent, 0.16)} />
        <SkeletonLines x={10} y={50} w={48} />
      </g>
      <g transform="translate(228 44) rotate(8)">
        <rect width="70" height="86" rx="10" fill={hsl(C.card)} stroke={hsl(C.border)} strokeWidth="1.4" />
        <rect x="10" y="12" width="26" height="26" rx="7" fill={hsl(C.primary, 0.16)} />
        <SkeletonLines x={10} y={50} w={48} />
      </g>
      {/* dönen yörünge noktaları */}
      <g className="ss-orbit">
        <circle cx="160" cy="110" r="60" fill="none" stroke={hsl(C.accent, 0.5)} strokeWidth="1.4" strokeDasharray="3 9" strokeLinecap="round" />
        <circle cx="220" cy="110" r="5" fill={hsl(C.accent)} />
      </g>
      {/* kilit */}
      <g>
        <circle cx="160" cy="110" r="46" fill={hsl(C.card)} stroke={hsl(C.accent, 0.9)} strokeWidth="2.2" />
        <path d="M144 106 V96 a16 16 0 0 1 32 0 V106" fill="none" stroke={hsl(C.fg, 0.85)} strokeWidth="7" strokeLinecap="round" />
        <rect x="134" y="104" width="52" height="40" rx="11" fill={hsl(C.accent)} />
        <circle cx="160" cy="121" r="6" fill={hsl('200 95% 14%')} />
        <rect x="157.2" y="123" width="5.6" height="12" rx="2.8" fill={hsl('200 95% 14%')} />
      </g>
    </Stage>
  )
}

export function ComingSoonArt() {
  const uid = useId().replace(/:/g, '')
  const c = 2 * Math.PI * 86
  return (
    <Stage tone="primary" uid={uid}>
      <Rings r={[60]} opacity={0.5} />
      {/* ilerleme halkası */}
      <circle cx="160" cy="110" r="86" fill="none" stroke={hsl(C.border)} strokeWidth="6" strokeOpacity="0.7" />
      <circle cx="160" cy="110" r="86" fill="none" stroke={hsl(C.primary)} strokeWidth="6" strokeLinecap="round"
        strokeDasharray={`${c * 0.72} ${c}`} transform="rotate(-100 160 110)" />
      {/* yığılan bloklar */}
      <g>
        <rect x="104" y="124" width="52" height="40" rx="10" fill={hsl(C.card)} stroke={hsl(C.border)} strokeWidth="1.6" />
        <rect x="164" y="124" width="52" height="40" rx="10" fill={hsl(C.card)} stroke={hsl(C.border)} strokeWidth="1.6" />
        <rect x="114" y="136" width="20" height="5" rx="2.5" fill={hsl(C.accent, 0.7)} />
        <rect x="174" y="136" width="32" height="5" rx="2.5" fill={hsl(C.muted, 0.5)} />
        <rect x="174" y="147" width="18" height="5" rx="2.5" fill={hsl(C.muted, 0.35)} />
        <rect x="114" y="147" width="30" height="5" rx="2.5" fill={hsl(C.muted, 0.35)} />
      </g>
      <g className="ss-drop">
        <rect x="134" y="76" width="52" height="40" rx="10" fill={hsl(C.primary, 0.14)} stroke={hsl(C.primary)} strokeWidth="2"
          strokeDasharray="5 5" />
        <path d="M160 88 v16 M152 96 h16" stroke={hsl(C.primary)} strokeWidth="3" strokeLinecap="round" />
      </g>
      <g fill={hsl(C.primary)}>
        <path d="M252 58 l2.6 6.4 6.4 2.6 -6.4 2.6 -2.6 6.4 -2.6 -6.4 -6.4 -2.6 6.4 -2.6z" className="ss-pulse" />
        <circle cx="92" cy="74" r="2.6" opacity="0.8" />
        <circle cx="244" cy="150" r="2.2" opacity="0.6" />
      </g>
    </Stage>
  )
}
